import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { TrendingUp, TrendingDown, Activity, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';


import { BACKEND_URL } from '../../config/api';
const ImpactSummary = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('lms_token');
      const response = await axios.get(`${BACKEND_URL}/api/mentor/impact/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching impact summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckAllPending = async () => {
    try {
      setChecking(true);
      const token = localStorage.getItem('lms_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/mentor/impact/check-all-pending`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show results
      alert(`Impact Check Complete!\n\nChecked: ${response.data.checked}\nHigh Impact: ${response.data.high_impact}\nModerate: ${response.data.moderate_impact}\nPoor Impact: ${response.data.poor_impact}\nAutomated Actions: ${response.data.automated_actions}`);
      
      // Refresh summary
      fetchSummary();
    } catch (error) {
      console.error('Error checking impacts:', error);
      alert('Error checking impacts. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Card>
    );
  }

  if (!summary || summary.total_checked === 0) {
    return (
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
        <div className="text-center">
          <Activity className="w-12 h-12 text-blue-600 mx-auto mb-3" />
          <h3 className="font-bold text-lg text-gray-900 mb-2">Impact Checker System</h3>
          <p className="text-sm text-gray-600 mb-4">
            No follow-ups checked yet. The system automatically monitors student activity 24-48 hours after calls.
          </p>
          <button
            onClick={handleCheckAllPending}
            disabled={checking}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50"
          >
            {checking ? 'Checking...' : 'Check All Pending Impacts'}
          </button>
        </div>
      </Card>
    );
  }

  const highPercentage = (summary.high_impact / summary.total_checked) * 100;
  const moderatePercentage = (summary.moderate_impact / summary.total_checked) * 100;
  const poorPercentage = (summary.poor_impact / summary.total_checked) * 100;

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-white border-2 border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Checked</p>
              <p className="text-3xl font-bold text-gray-900">{summary.total_checked}</p>
            </div>
            <Activity className="w-8 h-8 text-gray-400" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">High Impact</p>
              <p className="text-3xl font-bold text-green-700">{summary.high_impact}</p>
              <p className="text-xs text-green-600 mt-1">{highPercentage.toFixed(0)}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-600" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Moderate</p>
              <p className="text-3xl font-bold text-yellow-700">{summary.moderate_impact}</p>
              <p className="text-xs text-yellow-600 mt-1">{moderatePercentage.toFixed(0)}%</p>
            </div>
            <Activity className="w-8 h-8 text-yellow-600" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-red-50 to-pink-50 border-2 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Poor Impact</p>
              <p className="text-3xl font-bold text-red-700">{summary.poor_impact}</p>
              <p className="text-xs text-red-600 mt-1">{poorPercentage.toFixed(0)}%</p>
            </div>
            <TrendingDown className="w-8 h-8 text-red-600" />
          </div>
        </Card>
      </div>

      {/* Average Score & Actions */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg mb-2">Average Impact Score</h3>
            <div className="flex items-center gap-4">
              <div className="text-5xl font-bold text-blue-600">
                {summary.average_score.toFixed(1)}
              </div>
              <div>
                <p className="text-sm text-gray-600">out of 100</p>
                <p className="text-sm font-medium">
                  {summary.average_score > 70 ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Excellent Performance
                    </span>
                  ) : summary.average_score > 40 ? (
                    <span className="text-yellow-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      Good Performance
                    </span>
                  ) : (
                    <span className="text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      Needs Improvement
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={handleCheckAllPending}
            disabled={checking}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${checking ? 'animate-spin' : ''}`} />
            {checking ? 'Checking...' : 'Check All Pending'}
          </button>
        </div>

        {/* Score Breakdown Bar */}
        <div className="mt-6">
          <div className="flex gap-1 h-4 rounded-full overflow-hidden">
            {summary.high_impact > 0 && (
              <div
                className="bg-green-500"
                style={{ width: `${highPercentage}%` }}
                title={`High: ${summary.high_impact}`}
              ></div>
            )}
            {summary.moderate_impact > 0 && (
              <div
                className="bg-yellow-500"
                style={{ width: `${moderatePercentage}%` }}
                title={`Moderate: ${summary.moderate_impact}`}
              ></div>
            )}
            {summary.poor_impact > 0 && (
              <div
                className="bg-red-500"
                style={{ width: `${poorPercentage}%` }}
                title={`Poor: ${summary.poor_impact}`}
              ></div>
            )}
          </div>
          <div className="flex justify-between text-xs text-gray-600 mt-2">
            <span>0 (Poor)</span>
            <span>40</span>
            <span>70</span>
            <span>100 (High)</span>
          </div>
        </div>
      </Card>

      {/* Info Box */}
      <Card className="p-4 bg-blue-50 border-2 border-blue-200">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-bold text-sm text-blue-900 mb-1">How Impact Checker Works</h4>
            <p className="text-xs text-blue-800">
              The system automatically monitors student activity 24-48 hours after your follow-up calls. It tracks 6 metrics: login activity, progress increase, lessons completed, quiz attempts, video watch time, and streak improvement. Students with scores below 40 are automatically added to high-priority tasks and marked for immediate attention.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ImpactSummary;
